import React, { createContext, useState, useContext, useCallback, useMemo } from "react";
import ToastMsg from "../../utils/toastSuccess";

export interface ToastItem {
  id: number;
  message: string;
  bg?: string;
  Toastheader?: string;
  show: boolean;
}

const ToastContext = createContext<any>({} as any);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Add a new toast supporting:
  // 1. addToast("Success message", "success")
  // 2. addToast({ message: "Success message", bg: "success" })
  // 3. addToast({ message: "Error message", type: "error" })
  const addToast = useCallback((toastOrMessage: any, maybeBg?: string) => {
    let message = "";
    let bg = "success";
    let Toastheader: string | undefined = undefined;

    if (typeof toastOrMessage === "string") {
      message = toastOrMessage;
      bg = maybeBg || "success";
    } else if (typeof toastOrMessage === "object" && toastOrMessage !== null) {
      message = toastOrMessage.message || toastOrMessage.msg || toastOrMessage.text || "";
      bg = toastOrMessage.bg || toastOrMessage.type || maybeBg || "success";
      Toastheader = toastOrMessage.Toastheader || toastOrMessage.header || toastOrMessage.title;
    }

    if (!message) return;

    // Normalize bg aliases
    const lowerBg = (bg || "").toLowerCase();
    if (lowerBg === "error") bg = "danger";
    else if (lowerBg === "warn") bg = "warning";
    else if (lowerBg === "light" || lowerBg === "default") bg = "success";

    const newToast: ToastItem = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      message,
      bg,
      Toastheader,
      show: true,
    };

    setToasts((prev) => {
      // Prevent rapid identical duplicate toast spamming
      const isDuplicate = prev.some((t) => t.message === message && t.show);
      if (isDuplicate) return prev;
      return [...prev, newToast];
    });
  }, []);

  const hideToast = useCallback((id: number) => {
    setToasts((prev) =>
      prev.map((toast) => (toast.id === id ? { ...toast, show: false } : toast))
    );

    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 350);
  }, []);

  const contextValue = useMemo(() => ({ addToast, hideToast }), [addToast, hideToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div
        className="cht-fixed cht-top-5 cht-right-5 cht-flex cht-flex-col cht-items-end cht-gap-3 cht-pointer-events-none"
        style={{ zIndex: 999999 }}
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="cht-pointer-events-auto">
            <ToastMsg
              onClose={() => hideToast(toast.id)}
              message={toast.message}
              Toastheader={toast.Toastheader}
              show={toast.show}
              bg={toast.bg || "success"}
              index={toast.id}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): any => useContext(ToastContext);
